import React, { useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import i18n from '@/i18n';
import { Button } from '@/components/ui/atoms/Button';
import { useUploadResumesToBank } from '../../hooks/useJobOffers';
import { FileUpload, CV_SUPPORTED_EXTENSIONS, formatFileSize } from '../ui/FileUpload';

interface UploadToResumeBankModalProps {
  onClose: () => void;
}

export const UploadToResumeBankModal: React.FC<UploadToResumeBankModalProps> = ({ onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const uploadMutation = useUploadResumesToBank();

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error(i18n.t('job:toasts.upload_resume_select_required'));
      return;
    }

    try {
      const uploadMessage = selectedFiles.length === 1
        ? i18n.t('job:add_cv_modal.toast_uploading_one')
        : i18n.t('job:add_cv_modal.toast_uploading_other', { count: selectedFiles.length });
      const uploadToastId = toast.loading(uploadMessage);

      await uploadMutation.mutateAsync(selectedFiles);

      toast.dismiss(uploadToastId);
      
      const processingMessage = i18n.t('job:toasts.cv_processing');
      
      // Use a fixed ID so App.tsx can dismiss it when batch completes
      toast.loading(processingMessage, { 
        id: 'resume-bank-processing',
        duration: Infinity, // Stay until dismissed
      });
      
      // Close modal - notification will come from App.tsx when batch completes
      onClose();
      
    } catch (error) {
      console.error("[RESUME BANK] Upload error:", error);
      const unexpected = i18n.t('common:toasts.unexpected_error');
      const rawDetail = error instanceof Error ? error.message.trim() : '';
      const detail =
        rawDetail && rawDetail !== i18n.t('common:toasts.unexpected_error', { lng: 'en' })
          ? rawDetail
          : unexpected;
      const errorMessage = i18n.t('common:toasts.resume_failed_with_detail', {
        message: i18n.t('job:toasts.cv_upload_failed'),
        detail,
      });
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-dark-invirtus font-heading">Upload Resumes to Bank</h3>
          <Button
            type="icon"
            intent="muted"
            onClick={onClose}
            disabled={uploadMutation.isPending}
            label="Close"
            icon={<X/>}
          />
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-dark-invirtus mb-1 font-heading">Upload Resumes</h4>
          <p className="text-dark-invirtus/80 text-sm mb-4 font-body">
            Upload resumes to your searchable resume bank. These will be available for matching with future job postings. Each resume will be automatically processed and indexed for semantic search.
          </p>

          <FileUpload
            onFilesSelected={handleFilesSelected}
            onFileRemove={removeFile}
            selectedFiles={selectedFiles}
            acceptedFormats={CV_SUPPORTED_EXTENSIONS}
            multiple={true}
            disabled={uploadMutation.isPending}
            placeholder="Drag and drop resume files here, or click to browse"
            description={`Supports PDF, DOCX, TXT formats (Max: ${formatFileSize(10 * 1024 * 1024)} per file)`}
            buttonText="Select Resume Files"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            label="Cancel"
            type="outlined"
            intent="muted"
            onClick={onClose}
            disabled={uploadMutation.isPending}
          />
          <Button
            label={
              uploadMutation.isPending
                ? 'Uploading...'
                : `Upload ${selectedFiles.length} Resume${selectedFiles.length !== 1 ? 's' : ''}`
            }
            type="contained"
            intent="secondary"
            onClick={handleUpload}
            disabled={uploadMutation.isPending || selectedFiles.length === 0}
            loading={uploadMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
};
